<template>
    <form-modal
        ref="formModal"
        :handle-open="resetValidation"
        :v="$v.modalTeam"
        :title="i18nTitleModal"
        :in-editing="isEdit"
        size="xl"
        :handle-close="handleCancel"
    >
        <template v-slot:form>
            <form @submit.prevent="handleOk">
                <confirmation-form-modal
                    ref="confirmationFormModal"
                    :title="i18nTeam"
                    :operation="isEdit ? modalOperation : 'deleted'"
                />
                <feedback-modal
                    ref="feedbackModal"
                    :title="i18nTeam"
                />
                <b-row v-if="!isEdit" class="mb-2">
                    <div class="col-sm-6 form-inline">
                    </div>
                    <div class="col-sm-6">
                        <div class="text-sm-right">
                            <edit-label-button
                                v-if="$can('UPDATE', 'TEAM')"
                                :title="getI18n(i18nKey, 'TITLES.team')"
                                :disabled="deleteLoading"
                                @onClick="isEdit = true"
                            />
                           <delete-label-button
                                v-if="$can('DELETE', 'TEAM')"
                                :title="getI18n(i18nKey, 'TITLES.team')"
                                :busy="deleteLoading"
                                @onClick="handleDelete"
                            />
                        </div>
                    </div>
                </b-row>
                <b-tabs
                    id="teams-tabs"
                    v-model="tabIndex"
                    class="tabs-pdv"
                    @activate-tab="tabActivated">
                    <b-tab :title="i18nInformation">
                        <b-form-row>
                            <b-col md="8">
                                <b-form-group
                                    :label="i18nName"
                                    class="required label-pdv"
                                    label-for="modal-name"
                                    :invalid-feedback="getI18n('ERROR_CODES.invalid_name')"
                                >
                                    <b-form-input
                                        id="modal-name"
                                        v-model.trim="$v.modalTeam.name.$model"
                                        class="input-pdv-blue"
                                        name="name"
                                        aria-describedby="input-1-live-feedback"
                                        type="text"
                                        :state="validateField('name', 'modalTeam')"
                                        :placeholder="i18nName"
                                        autofocus
                                        autocomplete="off"
                                        :disabled="!isEdit || submitLoading"
                                        @blur="$v.modalTeam.name.$touch"
                                        @input="clearResponseError('name', 'modalTeam')"
                                    />
                                </b-form-group>
                            </b-col>
                            <b-col md="4">
                                <b-form-group
                                    :label="i18nSupervisor"
                                    class="required label-pdv"
                                    label-for="modal-leader"
                                    :invalid-feedback="getI18n('ERROR_CODES.invalid_supervisor')"
                                >
                                    <multiSelectWithService
                                        :id="'modal-leader'"
                                        ref="leaderMultiselect"
                                        v-model="modalTeam.leader"
                                        :input-function="$v.modalTeam.leader_id.$touch"
                                        :select-class="validationClass($v.modalTeam.leader_id)"
                                        :service="'users'"
                                        :searchable="true"
                                        :multiple="false"
                                        :disabled="!isEdit || submitLoading"
                                        :parameters="usersParameters"
                                        @input="changeLeaderId"
                                    />
                                </b-form-group>
                            </b-col>
                        </b-form-row>
                        <b-form-row v-if="isMirrorTeam && isEdit">
                            <b-col md="12">
                                <b-form-checkbox
                                    id="disable-mirror"
                                    v-model="disableMirror"
                                    class="mb-2"
                                    :disabled="submitLoading"
                                >
                                    {{ i18nDisableMirror }}
                                </b-form-checkbox>
                                <small class="form-text text-muted d-block">{{ i18nDisableMirrorHelp }}</small>
                            </b-col>
                        </b-form-row>
                        <b-form-row v-if="isEdit && !isTeamCreated">
                            <b-col md="12">
                                <b-form-checkbox
                                    id="create-as-mirror"
                                    v-model="createAsMirror"
                                    class="mb-2"
                                    :disabled="submitLoading"
                                    @change="onToggleCreateAsMirror"
                                >
                                    {{ i18nCreateAsMirror }}
                                </b-form-checkbox>
                            </b-col>
                        </b-form-row>
                        <b-form-row v-if="showMirrorBaseTeamSelect">
                            <b-col md="12">
                                <b-form-group
                                    :label="i18nBaseTeam"
                                    class="required label-pdv"
                                    label-for="modal-base-team"
                                >
                                    <multiSelectWithService
                                        id="modal-base-team"
                                        ref="baseTeamMultiselect"
                                        v-model="mirrorBaseTeam"
                                        :service="'teams'"
                                        :searchable="true"
                                        :multiple="false"
                                        :disabled="submitLoading"
                                        :parameters="teamsBaseParameters"
                                        label="name"
                                    />
                                </b-form-group>
                            </b-col>
                        </b-form-row>
                    </b-tab>

                    <b-tab :title="i18nUser">
                        <b-alert
                            v-if="mirrorMembersLocked"
                            show
                            variant="info"
                            class="mb-3"
                        >
                            {{ i18nMirrorMembersHint }}
                            <template v-if="modalTeam.base_team">
                                <strong>{{ modalTeam.base_team.name }}</strong>
                            </template>
                        </b-alert>
                        <b-alert
                            v-if="isMirrorCreate"
                            show
                            variant="secondary"
                            class="mb-3"
                        >
                            {{ i18nMirrorCreateNote }}
                        </b-alert>
                        <b-form-row>
                            <b-col v-if="isEdit && !mirrorMembersLocked" class="col-sm-12 col-md-4">
                                <b-form-group
                                    :label="getI18n('USERS.TITLES.user')"
                                    label-for="add-user-input"
                                    class="label-pdv" >
                                    <multiSelectWithService
                                        :id="'add-user-input'"
                                        ref="userMultiselect"
                                        v-model="userName"
                                        :service="'users'"
                                        :searchable="true"
                                        :multiple="false"
                                        :disabled="submitLoading"
                                        :parameters="usersParameters"
                                        :options-to-remove-list="teamMemberIdsExcludedFromCombo"
                                        :hide-selected="true"
                                    />
                                </b-form-group>
                            </b-col>
                            <b-col :class="isEdit && !mirrorMembersLocked ? 'col-sm-12 col-md-8' : 'col-sm-12 col-md-12'">
                                <data-mode-vuetable
                                    ref="relationVuetable"
                                    :value="teamMembersTableUsers"
                                    :fields="fields"
                                    :busy="loadingUsers"
                                    :display-delete-button="isEdit && !mirrorMembersLocked"
                                    :disabled="submitLoading"
                                    @onClickDelete="handleRemoveUser"
                                />
                            </b-col>
                        </b-form-row>
                    </b-tab>
                </b-tabs>
            </form>
        </template>

        <template v-slot:footer>
            <div v-if="isEdit" class="d-flex justify-content-center">
                <cancel-button
                    ref="cancelButton"
                    child-class="col-sm-4 float-left"
                    :disabled="submitLoading"
                    @onClick="handleCancel"
                />
                <save-button
                    v-if="tabIndex === 0 && isMirrorCreate"
                    ref="okButton"
                    child-class="col-sm-4 float-right ml-3"
                    :title="i18nSaveButton"
                    :busy="submitLoading"
                    :disabled="!canGoMirrorCreateSubmit"
                    @onClick="handleOk"
                />
                <save-button
                    v-else-if="tabIndex === 0"
                    ref="okButton"
                    child-class="col-sm-4 float-right ml-3"
                    :title="getI18n('COMMON.next')"
                    :busy="submitLoading"
                    :disabled="!canGoNext"
                    @onClick="goToNextTab"
                />
                <add-button
                    v-else
                    ref="okButton"
                    child-class="col-sm-4 float-right ml-3"
                    :busy="submitLoading"
                    @onClick="handleOk"
                />
            </div>
        </template>
    </form-modal>
</template>

<script>
    import AddButton from '@/src/components/AddButton'
import CancelButton from '@/src/components/CancelButton'
import DataModeVuetable from '@/src/components/DataModeVuetable.vue'
import DeleteLabelButton from '@/src/components/DeleteLabelButton.vue'
import EditLabelButton from '@/src/components/EditLabelButton.vue'
import SaveButton from '@/src/components/SaveButton'
import FormModal from '@components/FormModal'
import multiSelectWithService from '@components/multiSelectWithService'
import ConfirmationFormModal from '@src/components/ConfirmationFormModal'
import FeedbackModal from '@src/components/FeedbackModal'
import swalFeedback from '@src/mixins/swalFeedback'
import validationMixin from '@src/mixins/validation'
import TeamsService from '@src/services/TeamsService'
import { maxLength, minLength, numeric, required, requiredIf } from 'vuelidate/lib/validators'

    const i18nCommon = 'COMMON'
    const i18nKey = 'TEAMS'

    export default {
        components: {
            FormModal,
            ConfirmationFormModal,
            FeedbackModal,
            multiSelectWithService,
            CancelButton,
            AddButton,
            SaveButton,
            EditLabelButton,
            DeleteLabelButton,
            DataModeVuetable
        },
        mixins: [validationMixin, swalFeedback],
        props: {
            modalIsEdit: {
                required: true,
                type: Boolean,
            },
            modal: {
                type: Object,
                default: () => {
                    return {
                        id: 0,
                        name: '',
                        leader_id: null,
                        leader: null,
                        users: [],
                        mirror_of_team_id: null,
                        base_team: null,
                    }
                },
            },
        },
        data() {
            return {
                i18nCommon,
                i18nKey,
                modalTeam: this.modal,
                createAsMirror: false,
                mirrorBaseTeam: null,
                disableMirror: false,
                teamMembers: {users: []},
                users: [],
                usersLoaded: false,
                loadingUsers: false,
                filteredUsers: [],
                isEdit: false,
                submitLoading: false,
                deleteLoading: false,
                userName: '',
                tabIndex: 0,
                fields: [
                    {
                        key: 'name',
                        label: this.getI18n('COMMON.user_name'),
                        sortable: true,
                    },
                    {
                        key: 'email',
                        label: this.getI18n('COMMON.email'),
                        sortable: true,
                    },
                    {
                        key: 'actions',
                        label: '',
                    },
                ],
                usersParameters: {
                    per_page: 1000,
                    page: 1,
                    orderBy: 'name',
                    sortedBy: 'asc',
                    active: true,
                },
            }
        },
        validations: {
            modalTeam: {
                name: {
                    required,
                    minLength: minLength(2),
                    maxLength: maxLength(255),
                },
                leader_id: {
                    required,
                    numeric,
                },
            },
            teamMembers:{
                users: {
                    required: requiredIf(function () {
                        return !this.isMirrorCreate && !this.mirrorMembersLocked
                    }),
                    minLength: minLength(1),
                    $each: {
                        id: {
                            required,
                            numeric,
                        },
                    },
                },
            },
        },
        computed: {
            isMirrorTeam() {
                return !!(this.modalTeam.mirror_of_team_id)
            },
            mirrorMembersLocked() {
                return this.isMirrorTeam && !this.disableMirror
            },
            isMirrorCreate() {
                return !this.isTeamCreated && this.createAsMirror
            },
            showMirrorBaseTeamSelect() {
                return this.isEdit && !this.isTeamCreated && this.createAsMirror
            },
            teamsBaseParameters() {
                return {
                    per_page: 500,
                    page: 1,
                    orderBy: 'name',
                    sortedBy: 'asc',
                    only_base_teams: true,
                }
            },
            i18nCreateAsMirror() {
                return this.getI18n(i18nKey, 'BUTTONS.create_as_mirror')
            },
            i18nBaseTeam() {
                return this.getI18n(i18nKey, 'TITLES.base_team')
            },
            i18nMirrorMembersHint() {
                return this.getI18n(i18nKey, 'MESSAGES.mirror_members_managed_by_base')
            },
            i18nMirrorCreateNote() {
                return this.getI18n(i18nKey, 'MESSAGES.mirror_create_members_note')
            },
            i18nSaveButton() {
                return this.getI18n(i18nKey, 'BUTTONS.save')
            },
            i18nDisableMirror() {
                return this.getI18n(i18nKey, 'BUTTONS.disable_mirror')
            },
            i18nDisableMirrorHelp() {
                return this.getI18n(i18nKey, 'MESSAGES.disable_mirror_help')
            },
            isTeamCreated() {
                return this.modalTeam.id > 0
            },
            i18nTitleModal() {
                if (this.isTeamCreated) {
                    if (this.isEdit) {
                        return `${this.getI18n(i18nCommon, 'edit')} ${this.i18nTeam}`
                    }
                    return this.modalTeam.name
                }
                else {
                    return this.i18nNewTeam
                }
            },
            i18nTeam() {
                return this.getI18n(i18nKey, 'TITLES.team')
            },
            i18nNewTeam() {
                return this.getI18n(i18nKey, 'BUTTONS.new_team')
            },
            i18nName() {
                return this.getI18n(i18nCommon, 'name')
            },
            i18nSupervisor() {
                return this.getI18n(i18nCommon, 'supervisor')
            },
            i18nUser() {
                return this.getI18nModified({
                    prefix: 'USERS.TITLES.user',
                    modifier: 2,
                })
            },
            i18nInformation() {
                return this.getI18nModified({
                    prefix: i18nCommon,
                    suffix: 'info',
                    modifier: 2,
                })
            },
            modalOperation() {
                return this.isTeamCreated ? 'edited' : 'created'
            },
            selectedUsers() {
                return this.teamMembers.users
            },
            canGoNext() {
                return (
                    this.modalTeam.name &&
                    this.modalTeam.leader_id &&
                    !this.submitLoading
                )
            },
            canGoMirrorCreateSubmit() {
                return (
                    this.modalTeam.name &&
                    this.modalTeam.leader_id &&
                    this.mirrorBaseTeam &&
                    this.mirrorBaseTeam.id &&
                    !this.submitLoading
                )
            },
            teamMemberIdsExcludedFromCombo() {
                const ids = this.teamMembers.users.map((user) => user.id)

                if (this.modalTeam.leader_id) {
                    ids.push(this.modalTeam.leader_id)
                }

                return [...new Set(ids)]
            },
            teamMembersTableUsers() {
                if (!this.isEdit || this.mirrorMembersLocked) {
                    return this.teamMembers.users
                }

                return this.dedupeUsersById(this.teamMembers.users)
            },
        },
        watch: {
            userName(value) {
                if (value) {
                    const index = this.teamMembers.users.findIndex(find => find.id === value.id);
                    if (value.id === this.modalTeam.leader_id) {
                        this.warningFeedback(this.i18nTeam, this.getI18n(this.i18nKey, 'MESSAGES.it_is_not_allowed_to_link_the_team_supervisor'))
                    }
                    else if (index >= 0) {
                        this.warningFeedback(this.i18nTeam, this.getI18n(this.i18nKey, 'MESSAGES.user_already_linked_to_team'))
                    }
                    else if (index < 0) {
                        this.teamMembers.users.push(value)
                    }
                    this.userName = ''
                    this.$refs.userMultiselect.$el.focus()
                }
            },
            'modalTeam.leader_id': function (newValue) {
                this.filteredUsers = this.users.filter(
                    (user) => user.id !== newValue
                )
                this.teamMembers.users = this.teamMembers.users.filter(
                    (user) => user.id !== newValue
                )
            },
            isEdit(value) {
                if (value) {
                    this.loadUsers()
                }
            },
            teamMemberIdsExcludedFromCombo() {
                this.refreshUserMultiselectOptions()
            },
            'teamMembers.users': {
                deep: true,
                handler() {
                    this.refreshUserMultiselectOptions()
                },
            },
        },
        methods: {
            dedupeUsersById(users) {
                const seen = new Set()

                return users.filter((user) => {
                    if (seen.has(user.id)) {
                        return false
                    }

                    seen.add(user.id)

                    return true
                })
            },
            refreshUserMultiselectOptions() {
                if (!this.isEdit || !this.$refs.userMultiselect) {
                    return
                }

                this.$refs.userMultiselect.loadOptions()
            },
            tabActivated(newTabIndex, oldTabIndex, event) {
                if (this.isEdit) {
                    if (oldTabIndex === 0) {
                        this.$v.modalTeam.$touch()
                        if (this.$v.modalTeam.$invalid) {
                            event.preventDefault();
                            return
                        }
                        if (this.isMirrorCreate && (!this.mirrorBaseTeam || !this.mirrorBaseTeam.id)) {
                            event.preventDefault();
                            return
                        }
                    }
                }
                if (newTabIndex === 1) {
                    this.loadUsers()
                }
            },
            onToggleCreateAsMirror() {
                if (!this.createAsMirror) {
                    this.mirrorBaseTeam = null
                }
            },
            changeLeaderId(e) {
                this.modalTeam.leader_id = e ? e.id : null
                this.clearResponseError('leader', 'modalTeam')
            },
            showModal() {
                this.isEdit = this.modalIsEdit
                this.submitLoading = false
                this.deleteLoading = false
                this.modalTeam = this.modal
                this.createAsMirror = false
                this.mirrorBaseTeam = null
                this.disableMirror = false
                this.usersLoaded = false
                this.loadingUsers = false
                this.teamMembers.users = []
                this.userName = ''
                this.tabIndex = 0
                this.$refs.formModal.show()
            },
            async loadUsers() {
                if (this.usersLoaded || this.loadingUsers) {
                    return
                }

                if (!this.isTeamCreated) {
                    this.teamMembers.users = []
                    this.usersLoaded = true
                    return
                }

                this.loadingUsers = true
                await TeamsService.fetchById(this.modalTeam.id, {
                    with: ['users', 'baseTeam'],
                }
                ).then((response) => {
                    const payload = response.data.data
                    this.teamMembers.users = payload.users
                    this.modalTeam.base_team = payload.base_team || null
                    this.usersLoaded = true
                })
                this.loadingUsers = false
            },
            resetValidation() {
                this.$nextTick(() => this.$v.$reset())
                this.clearAllResponseError()
            },
            cleanModal() {
                this.replaceModalData({
                    id: '',
                    name: '',
                    leader_id: null,
                    leader: null,
                    mirror_of_team_id: null,
                    base_team: null,
                })
                this.createAsMirror = false
                this.mirrorBaseTeam = null
                this.disableMirror = false
                this.resetValidation()
            },
            replaceModalData(data) {
                Object.assign(this.modalTeam, data)
                this.teamMembers.users = [];
            },
            handleCancel() {
                if (this.isEdit) {
                    this.$refs.confirmationFormModal.showModal(true, this.handleClose)
                }
                else {
                    this.handleClose(true)
                }
            },
            handleClose(close) {
                if (!close) {
                    return;
                }
                this.resetValidation()
                this.$refs.formModal.hide()
            },
            handleOk() {
                this.$v.modalTeam.$touch()
                if (!this.isMirrorCreate && !this.mirrorMembersLocked) {
                    this.$v.teamMembers.$touch()
                }
                if (this.$v.modalTeam.$invalid || this.submitLoading) {
                    return
                }
                if (!this.isMirrorCreate && !this.mirrorMembersLocked && this.$v.teamMembers.$invalid) {
                    return
                }
                if (this.isMirrorCreate && (!this.mirrorBaseTeam || !this.mirrorBaseTeam.id)) {
                    return
                }
                this.$refs.confirmationFormModal.showModal(false, this.handleSubmit)
            },
            async handleSubmit(submit) {
                if (!submit) {
                    return;
                }

                this.submitLoading = true

                const formattedTeam = Object.assign({}, this.modalTeam)

                if (this.isMirrorCreate) {
                    formattedTeam.mirror_of_team_id = this.mirrorBaseTeam.id
                    delete formattedTeam.users
                } else if (this.mirrorMembersLocked) {
                    delete formattedTeam.users
                } else {
                    formattedTeam.users = [
                        ...new Set(
                            this.teamMembers.users.map((user) => user.id) || []
                        ),
                    ]
                    if (this.disableMirror && this.modalTeam.mirror_of_team_id) {
                        formattedTeam.disable_mirror = true
                    }
                }

                const operation = this.isTeamCreated
                    ? TeamsService.update(this.modalTeam.id, formattedTeam)
                    : TeamsService.create(formattedTeam)

                const createResponse = await operation
                    .then((response) => response)
                    .catch((error) => error)

                const statusCode = createResponse.status.toString()

                if (statusCode.charAt(0) === '2') {
                    this.$emit('refresh')
                    this.resetValidation()
                    this.teamMembers.users = []
                    this.usersLoaded = false
                    this.$refs.formModal.hide()
                    this.positiveFeedback(this.i18nTeam, this.modalOperation)
                }

                if (statusCode === '422') {
                    this.modalResponseErrors = createResponse.data.errors
                }
                this.submitLoading = false
            },
            handleDelete() {
                if (this.deleteLoading) {
                    return;
                }
                this.$refs.confirmationFormModal.showModal(false, this.deleteTeam)
            },
            async deleteTeam(deleteRecord) {
                if (!deleteRecord) {
                    return;
                }

                this.$v.modalTeam.$touch()
                this.deleteLoading = true
                const response = await TeamsService.delete(this.modalTeam.id)
                    .then((response) => response)
                    .catch((error) => error)

                const statusCode = response.status.toString()

                if (statusCode.charAt(0) === '2') {
                    this.$emit('refresh')
                    this.resetValidation()
                    this.teamMembers.users = [];
                    this.$refs.formModal.hide()
                    this.positiveFeedback(this.i18nTeam, 'deleted', 3)
                } else if (statusCode === '417') {
                    this.$refs.feedbackModal.showModal(statusCode, response.data.message)
                } else if (statusCode === '422') {
                    this.modalResponseErrors = response.data.errors
                } else {
                    this.$refs.feedbackModal.showModal(statusCode, response.data.message)
                }

                this.deleteLoading = false
            },
            handleRemoveUser(id) {
                this.teamMembers.users = this.teamMembers.users.filter(
                    (user) => user.id !== id
                )
            },
            goToNextTab() {
                this.tabIndex = 1
                this.loadUsers()
            }
        },
    }
</script>
